@if (infoCardsReady()) {
  <div class="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 mb-6 mt-10">
    @for (card of infoCards(); track card.title) {
      <app-info-card
        [title]="card.title"
        [value]="card.value"
        [valueColor]="card.valueColor"
        [description]="card.description"
      />
    }
  </div>
} @else if (loading()) {
  <div class="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 mb-6 mt-10">
    @for (i of [1,2,3,4]; track i) {
      <div class="bg-(--color-bg-primary) rounded-lg p-4 animate-pulse">
        <div class="h-3 w-20 bg-(--color-bg-secondary) rounded mb-3"></div>
        <div class="h-8 w-24 bg-(--color-bg-secondary) rounded mb-2"></div>
        <div class="h-3 w-16 bg-(--color-bg-secondary) rounded"></div>
      </div>
    }
  </div>
}

<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
  <div class="flex flex-col gap-4">
    @if (userProfileReady()) {
      <app-data-section [title]="userProfile()!.title" [icon]="userProfile()!.icon" [accentColor]="userProfile()!.accentColor">
        @for (row of userProfile()!.rows; track row.label) {
          <app-data-row [label]="row.label" [value]="row.value" />
        }
      </app-data-section>
    } @else if (loading()) {
      <div class="bg-(--color-bg-primary) rounded-lg p-4 animate-pulse">
        <div class="flex items-center gap-3 mb-4 pb-3 border-b border-(--color-border)">
          <div class="w-8 h-8 bg-(--color-bg-secondary) rounded-md"></div>
          <div class="h-4 w-32 bg-(--color-bg-secondary) rounded"></div>
        </div>
        <div class="space-y-3">
          @for (i of [1,2,3,4]; track i) {
            <div class="flex gap-4">
              <div class="h-3 w-24 bg-(--color-bg-secondary) rounded"></div>
              <div class="h-3 flex-1 bg-(--color-bg-secondary) rounded"></div>
            </div>
          }
        </div>
      </div>
    }

    @if (photoEvidenceLoading()) {
      <div class="bg-(--color-bg-primary) rounded-lg p-4 animate-pulse">
        <div class="flex items-center gap-3 mb-4 pb-3 border-b border-(--color-border)">
          <div class="w-8 h-8 bg-(--color-bg-secondary) rounded-md"></div>
          <div class="h-4 w-36 bg-(--color-bg-secondary) rounded"></div>
        </div>
        <div class="grid grid-cols-3 gap-2">
          @for (i of [1,2,3,4,5,6]; track i) {
            <div class="aspect-square bg-(--color-bg-secondary) rounded-lg"></div>
          }
        </div>
      </div>
    } @else {
      <app-data-section [title]="'applicantRecord.photographicEvidence' | translate" icon="photo_camera" accentColor="warning">
        <app-photo-evidence [sections]="evidenceSections()" [loading]="photoEvidenceLoading()" />
      </app-data-section>
    }
  </div>

  <div class="flex flex-col gap-4">
    @if (locationDataReady()) {
      <app-data-section [title]="locationData()!.title" [icon]="locationData()!.icon" [accentColor]="locationData()!.accentColor">
        <div #mapContainer class="rounded-lg overflow-hidden mb-3" style="height: 350px;"></div>
        <div class="flex items-center gap-3 mb-3 px-1">
          <input
            type="range"
            min="25"
            max="100"
            step="5"
            [ngModel]="radiusMeters()"
            (ngModelChange)="radiusMeters.set($event)"
            class="flex-1 h-2 bg-(--color-bg-secondary) rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <span class="text-xs text-(--color-text-primary) font-medium min-w-[50px] text-right">{{ radiusMeters() }}m</span>
        </div>
        <div class="flex flex-wrap gap-2 mb-3">
          <button type="button" class="text-xs text-(--color-text-primary) px-2 py-1 rounded border border-(--color-border) hover:bg-(--color-bg-secondary) transition-colors" (click)="recenterMap()">
            {{ 'map.recenter' | translate }}
          </button>
          <button type="button" class="flex items-center gap-1.5 text-xs text-(--color-text-primary) px-2 py-1 rounded border border-(--color-border) hover:bg-(--color-bg-secondary) transition-colors" (click)="openGoogleMaps('main')">
            <span class="w-2 h-2 rounded-full" [style.backgroundColor]="markerColors.main"></span>
            {{ 'map.principal' | translate }}
          </button>
          <button type="button" class="flex items-center gap-1.5 text-xs text-(--color-text-primary) px-2 py-1 rounded border border-(--color-border) hover:bg-(--color-bg-secondary) transition-colors" (click)="openGoogleMaps('user')">
            <span class="w-2 h-2 rounded-full" [style.backgroundColor]="markerColors.user"></span>
            {{ 'map.user' | translate }}
          </button>
          <button type="button" class="flex items-center gap-1.5 text-xs text-(--color-text-primary) px-2 py-1 rounded border border-(--color-border) hover:bg-(--color-bg-secondary) transition-colors" (click)="openGoogleMaps('formulario')">
            <span class="w-2 h-2 rounded-full" [style.backgroundColor]="markerColors.formulario"></span>
            {{ 'map.form' | translate }}
          </button>
          <button type="button" class="flex items-center gap-1.5 text-xs text-(--color-text-primary) px-2 py-1 rounded border border-(--color-border) hover:bg-(--color-bg-secondary) transition-colors" (click)="openGoogleMaps('evidencias')">
            <span class="w-2 h-2 rounded-full" [style.backgroundColor]="markerColors.evidencias"></span>
            {{ 'map.evidence' | translate }}
          </button>
          <button type="button" class="text-xs text-(--color-text-primary) px-2 py-1 rounded border border-(--color-border) hover:bg-(--color-bg-secondary) transition-colors" (click)="fitAllMarkersMap()">
            {{ 'map.viewAll' | translate }}
          </button>
        </div>
        <div class="flex flex-col gap-2">
          @for (row of locationData()!.rows; track row.label) {
            <app-data-row [label]="row.label" [value]="row.value" />
          }
        </div>
      </app-data-section>
    } @else if (!locationDataReady()) {
      <div class="bg-(--color-bg-primary) rounded-lg p-4 animate-pulse">
        <div class="flex items-center gap-3 mb-4 pb-3 border-b border-(--color-border)">
          <div class="w-8 h-8 bg-(--color-bg-secondary) rounded-md"></div>
          <div class="h-4 w-28 bg-(--color-bg-secondary) rounded"></div>
        </div>
        <div class="rounded-lg overflow-hidden mb-3">
          <div class="h-37.5 bg-(--color-bg-secondary)"></div>
        </div>
        <div class="space-y-3">
          @for (i of [1,2,3,4]; track i) {
            <div class="flex gap-4">
              <div class="h-3 w-24 bg-(--color-bg-secondary) rounded"></div>
              <div class="h-3 flex-1 bg-(--color-bg-secondary) rounded"></div>
            </div>
          }
        </div>
      </div>
    }

    @if (personalReferencesReady()) {
      <app-data-section [title]="'applicantRecord.personalReferences' | translate" [icon]="'people'" [accentColor]="'danger'">
        @for (ref of personalReferences(); track ref.id) {
          <div class="flex items-start py-3 border-b border-(--color-border) last:border-b-0">
            <div class="flex flex-col gap-1">
              <span class="text-sm font-semibold text-(--color-text-primary)">{{ ref.nombre }}</span>
              <span class="text-xs text-(--color-text-secondary)">{{ ref.parentesco }}{{ ref.tiempoConocerla ? ' • ' + ref.tiempoConocerla : '' }}</span>
              <span class="text-xs text-(--color-text-secondary)">{{ ref.telefono }}</span>
            </div>
          </div>
        }
      </app-data-section>
    } @else if (loading()) {
      <div class="bg-(--color-bg-primary) rounded-lg p-4 animate-pulse">
        <div class="flex items-center gap-3 mb-4 pb-3 border-b border-(--color-border)">
          <div class="w-8 h-8 bg-(--color-bg-secondary) rounded-md"></div>
          <div class="h-4 w-40 bg-(--color-bg-secondary) rounded"></div>
        </div>
        <div class="space-y-3">
          @for (i of [1,3]; track i) {
            <div class="flex gap-4">
              <div class="h-3 w-24 bg-(--color-bg-secondary) rounded"></div>
              <div class="h-3 flex-1 bg-(--color-bg-secondary) rounded"></div>
            </div>
          }
        </div>
      </div>
    }

    @if (loading()) {
      <div class="bg-(--color-bg-primary) rounded-lg p-4 animate-pulse">
        <div class="flex items-center gap-3 mb-4 pb-3 border-b border-(--color-border)">
          <div class="w-8 h-8 bg-(--color-bg-secondary) rounded-md"></div>
          <div class="h-4 w-28 bg-(--color-bg-secondary) rounded"></div>
        </div>
        <div class="space-y-3 mb-4">
          @for (i of [1,2,3]; track i) {
            <div class="flex gap-4">
              <div class="h-3 w-24 bg-(--color-bg-secondary) rounded"></div>
              <div class="h-3 flex-1 bg-(--color-bg-secondary) rounded"></div>
            </div>
          }
        </div>
        <div class="h-20 bg-(--color-bg-secondary) rounded-lg mb-3"></div>
        <div class="flex justify-end">
          <div class="h-8 w-24 bg-(--color-bg-secondary) rounded-md"></div>
        </div>
      </div>
    } @else {
      <app-data-section [title]="'applicantRecord.comments' | translate" [icon]="'comment'" [accentColor]="'info'">
        @if (!validationTicketId()) {
          <div class="text-sm text-(--color-text-muted) mb-3">{{ 'applicantRecord.commentsUnavailable' | translate }}</div>
        } @else if (commentsLoading()) {
          <div class="space-y-3 mb-3">
            @for (i of [1,2,3]; track i) {
              <div class="flex gap-4">
                <div class="h-3 w-24 bg-(--color-bg-secondary) rounded"></div>
                <div class="h-3 flex-1 bg-(--color-bg-secondary) rounded"></div>
              </div>
            }
          </div>
        } @else if (commentsReady() && comments().length > 0) {
          <div class="flex flex-col gap-3 mb-4 max-h-48 overflow-y-auto">
            @for (comment of comments(); track comment.Id) {
              <div class="flex flex-col gap-1 p-3 bg-(--color-bg-secondary) rounded-lg">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-semibold text-(--color-text-primary)">{{ comment.Responsible }}</span>
                  <span class="text-xs text-(--color-text-muted)">{{ comment.CreateDate }}</span>
                </div>
                <span class="text-sm text-(--color-text-secondary)">{{ comment.Comment }}</span>
              </div>
            }
          </div>
        } @else if (commentsReady()) {
          <div class="text-sm text-(--color-text-muted) mb-3">{{ 'applicantRecord.noComments' | translate }}</div>
        }
        @if (validationTicketId()) {
          <textarea
            class="w-full min-h-20 p-3 border border-(--color-border) rounded-lg text-sm resize-y mb-3 bg-(--color-bg-primary) text-(--color-text-primary) focus:outline-none focus:border-(--color-primary)"
            [placeholder]="'applicantRecord.commentPlaceholder' | translate"
            [ngModel]="commentText()"
            (ngModelChange)="commentText.set($event)">
          </textarea>
          <div class="flex justify-end">
            <app-button
              type="primary"
              [text]="'common.actions.publish' | translate"
              icon="send"
              size="sm"
              [disabled]="!canPerformAction() || isSubmittingComment() || isActionLoading()"
              (buttonClick)="onSubmitComment()"
            />
          </div>
        }
      </app-data-section>
    }
  </div>
</div>